import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import axios from "axios";
import Header from "../Header/Header";
import { Table, Input, Label } from "reactstrap";
import moment from "moment";
import {
  IcArrowDown, IcArrowUp, IcWallet, IcPercent, IcUsers, IcHourglass,
} from "../../Icons";

const fmt = (v) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency", currency: "MXN", maximumFractionDigits: 0,
  }).format(Number(v) || 0);

function Inicio() {
  const { user } = useContext(AuthContext);
  const URL_ENTRADAS = process.env.REACT_APP_URL_ENTRADAS;
  const URL_SALIDAS = process.env.REACT_APP_URL_SALIDAS;
  const URL_CLIENTES = process.env.REACT_APP_URL_CLIENTES;
  const auth = { headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } };

  const [entradas, setEntradas] = useState([]);
  const [salidas, setSalidas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [mesSel, setMesSel] = useState("");

  // Se trae el último año y el tablero se para en el mes más reciente CON movimiento,
  // así no amanece en ceros el primer día del mes ni cuando se opera con rezago.
  const desde = moment().subtract(12, "months").startOf("month").format("YYYY-MM-DD");
  const hasta = moment().format("YYYY-MM-DD");

  useEffect(() => {
    axios.post(`${URL_ENTRADAS}/fechas`,
      { selectedFechaInicio: desde, selectedFechaFin: hasta }, auth)
      .then((r) => setEntradas(r.data)).catch(() => {});
    axios.post(`${URL_SALIDAS}/fechas`,
      { selectedFechaInicio: desde, selectedFechaFin: hasta }, auth)
      .then((r) => setSalidas(r.data)).catch(() => {});
    axios.get(URL_CLIENTES, auth)
      .then((r) => setClientes(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  const mesDe = (a) => (a.fecha || "").slice(0, 7);

  // Meses con movimiento, del más reciente al más viejo. Se agrega el mes en curso
  // aunque venga vacío, para poder consultarlo.
  const conMovimiento = [...new Set([...entradas, ...salidas].map(mesDe).filter(Boolean))]
    .sort()
    .reverse();
  const meses = [...new Set([...conMovimiento, moment().format("YYYY-MM")])]
    .sort()
    .reverse();

  // Por defecto se abre en el último mes con movimiento
  useEffect(() => {
    if (!mesSel && conMovimiento.length) setMesSel(conMovimiento[0]);
  }, [entradas, salidas]); // eslint-disable-line

  const mesActivo = mesSel || conMovimiento[0] || moment().format("YYYY-MM");

  const entradasMes = entradas.filter((a) => mesDe(a) === mesActivo);
  const salidasMes = salidas.filter((a) => mesDe(a) === mesActivo);

  const totalEntradas = entradasMes.reduce((s, a) => s + (a.monto || 0), 0);
  const totalComisiones = entradasMes.reduce((s, a) => s + (a.comisionTotal || 0), 0);
  const totalSalidas = salidasMes.reduce((s, a) => s + (a.monto || 0), 0);
  const pendientes =
    entradas.filter((a) => a.status === "Pendiente").length +
    salidas.filter((a) => a.status === "Pendiente").length;

  const activos = clientes.filter((a) => a.is_active === "Si");
  const saldoTotal = activos.reduce((s, a) => s + (a.saldo || 0), 0);

  const etiquetaMes = moment(mesActivo, "YYYY-MM").format("MMMM YYYY");

  const topClientes = [...activos]
    .sort((a, b) => (b.saldo || 0) - (a.saldo || 0))
    .slice(0, 8);

  const KPIS = [
    { label: `Entradas de ${etiquetaMes}`, value: fmt(totalEntradas), icon: IcArrowDown, color: "#10b981", bg: "#f0fdf4" },
    { label: `Comisiones de ${etiquetaMes}`, value: fmt(totalComisiones), icon: IcPercent, color: "#8b5cf6", bg: "#f5f3ff" },
    { label: `Salidas de ${etiquetaMes}`, value: fmt(totalSalidas), icon: IcArrowUp, color: "#dc2626", bg: "#fef2f2" },
    { label: "Saldo total clientes", value: fmt(saldoTotal), icon: IcWallet, color: "#3b82f6", bg: "#eff6ff" },
    { label: "Clientes activos", value: activos.length, icon: IcUsers, color: "#0ea5e9", bg: "#f0f9ff" },
    { label: "Movimientos pendientes", value: pendientes, icon: IcHourglass, color: "#f59e0b", bg: "#fffbeb" },
  ];

  return (
    <>
      <Header />
      <div className="card container col-12">
        <div className="page-header">
          <div>
            <h3 className="page-title">
              Hola, {user?.nombre || ""}
            </h3>
            <p className="page-subtitle">
              KSFX · {moment().format("D [de] MMMM YYYY")} · Cifras de {etiquetaMes}
            </p>
          </div>
          <div className="page-actions">
            <div style={{ display: "flex", flexDirection: "column" }}>
              <Label style={{ marginBottom: 2 }}>Mes</Label>
              <Input
                bsSize="sm"
                type="select"
                value={mesActivo}
                onChange={(e) => setMesSel(e.target.value)}
                style={{ minWidth: 170 }}
              >
                {meses.map((m) => (
                  <option key={m} value={m}>
                    {moment(m, "YYYY-MM").format("MMMM YYYY")}
                  </option>
                ))}
              </Input>
            </div>
          </div>
        </div>

        {/* ── KPIs ──────────────────────────────────────────────────────── */}
        <div className="row">
          {KPIS.map((k, i) => (
            <div key={i} className="col-12 col-md-6 col-xl-4 mb-3">
              <div className="kpi-card">
                <div className="kpi-icon" style={{ background: k.bg }}>
                  <k.icon style={{ color: k.color }} />
                </div>
                <div>
                  <div className="kpi-label">{k.label}</div>
                  <div className="kpi-value">{k.value}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Saldos por cliente ────────────────────────────────────────── */}
        <div className="row">
          <div className="col-md-6">
            <h5 style={{ fontSize: "0.9rem", fontWeight: 700, marginTop: 16 }}>
              Mayores saldos por cliente
            </h5>
            <Table size="sm" striped borderless className="table-responsive-xl table-dark-header">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th style={{ textAlign: "right" }}>Saldo</th>
                </tr>
              </thead>
              <tbody>
                {topClientes.map((a) => (
                  <tr key={a._id}>
                    <td>{a.nombre_comercial}</td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>{fmt(a.saldo)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <div className="col-md-6">
            <h5 style={{ fontSize: "0.9rem", fontWeight: 700, marginTop: 16 }}>
              Movimientos de {etiquetaMes}
            </h5>
            <Table size="sm" striped borderless className="table-responsive-xl table-dark-header">
              <thead>
                <tr>
                  <th>Movimiento</th>
                  <th>Fecha</th>
                  <th>Cliente</th>
                  <th style={{ textAlign: "right" }}>Monto</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ...entradasMes.map((a) => ({
                    tipo: "Entrada", fecha: a.fecha, monto: a.monto,
                    cliente: a.clientes?.[0]?.nombre_comercial, id: `e${a._id}`,
                  })),
                  ...salidasMes.map((a) => ({
                    tipo: "Salida", fecha: a.fecha, monto: a.monto,
                    cliente: a.clientes?.[0]?.nombre_comercial, id: `s${a._id}`,
                  })),
                ]
                  .sort((a, b) => (a.fecha < b.fecha ? 1 : -1))
                  .slice(0, 8)
                  .map((m) => (
                    <tr key={m.id}>
                      <td style={{
                        fontWeight: 600,
                        color: m.tipo === "Entrada" ? "#059669" : "#dc2626",
                      }}>
                        {m.tipo}
                      </td>
                      <td>{m.fecha}</td>
                      <td>{m.cliente}</td>
                      <td style={{ textAlign: "right" }}>{fmt(m.monto)}</td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </div>
        </div>
      </div>
    </>
  );
}

export default Inicio;
